import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Camera, ArrowLeft, ChevronLeft, ChevronRight, X, Download,
  Copy, Link2, Grid3x3, Layers, Trash2, Plus, Image as ImageIcon,
  Upload, Share2, Check, MessageCircle, Play, Film, Clock, ShoppingBag, Edit3
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { compressImageFile } from '../lib/image';
import { albumAPI } from '../lib/api';
import WatermarkOverlay from '../components/WatermarkOverlay';

export default function Gallery() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { currentUser, reloadAlbums } = useApp();
  const { toast } = useToast();

  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState('grid'); // grid | masonry
  const [lightbox, setLightbox] = useState(null); // index of open photo
  const [addOpen, setAddOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [purchases, setPurchases] = useState([]);

  // Merge server responses into state; keep existing photos when a response
  // (e.g. update/extend) comes back without them.
  const setLocalAlbum = useCallback((updated) => {
    if (!updated) return;
    setAlbum(prev => ({ ...prev, ...updated, photos: updated.photos ?? prev?.photos ?? [] }));
  }, []);

  // Load full album (with photos) directly from API
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    albumAPI.get(code)
      .then(a => { if (!cancelled) { setAlbum(a); setLoading(false); } })
      .catch(() => { if (!cancelled) { setAlbum(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    if (!album?.code) return;
    albumAPI.listPurchases(album.code).then(setPurchases).catch(() => setPurchases([]));
  }, [album?.code]);

  // Set of photo IDs that were purchased by any client
  const soldIds = useMemo(() => {
    const s = new Set();
    for (const p of purchases) {
      if (p.mode === 'full') {
        (album?.photos || []).forEach(ph => s.add(ph.id));
      } else {
        (p.photoIds || []).forEach(id => s.add(id));
      }
    }
    return s;
  }, [purchases, album]);

  const isOwner = currentUser && (!album?.ownerId || album?.ownerId === currentUser.id);
  // Defensive fallback — a freshly-created album, a server lag, or a
  // half-loaded doc can return `album` with `photos` missing. We never want
  // the gallery to crash the whole page on that case.
  const photos = album?.photos || [];

  useEffect(() => {
    if (!currentUser) navigate('/auth');
  }, [currentUser, navigate]);

  const openAt = useCallback((i) => setLightbox(i), []);
  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(() => setLightbox(i => (photos.length ? (i + 1) % photos.length : null)), [photos.length]);
  const prev = useCallback(() => setLightbox(i => (photos.length ? (i - 1 + photos.length) % photos.length : null)), [photos.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, close, next, prev]);

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="text-center text-[#a89e87]">Carregando álbum…</div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#f2ecdf] mb-3">Álbum não encontrado</h1>
          <Link to="/painel" className="btn-gold px-4 py-2 rounded-md text-sm font-medium inline-block">Voltar ao painel</Link>
        </div>
      </div>
    );
  }

  const copyPublicLink = () => {
    const url = `${window.location.origin}/album/${album.code}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copiado', description: url });
  };

  const extendLink = async () => {
    try {
      const updated = await albumAPI.extend(album.code, 24);
      setLocalAlbum(updated);
      await reloadAlbums();
      toast({ title: 'Acesso estendido', description: 'Válido por mais 24 horas.' });
    } catch (e) {
      toast({ title: 'Erro ao estender', description: e?.response?.data?.detail || '' });
    }
  };

  const removePhoto = async (id) => {
    if (!window.confirm('Remover esta foto do álbum?')) return;
    try {
      await albumAPI.removePhoto(album.code, id);
      const updated = await albumAPI.get(album.code);
      setLocalAlbum(updated);
      await reloadAlbums();
      toast({ title: 'Foto removida' });
    } catch (e) {
      toast({ title: 'Erro ao remover', description: e?.response?.data?.detail || 'Tente novamente' });
    }
  };

  const downloadPhoto = (photo) => {
    const a = document.createElement('a');
    a.href = photo.url;
    a.download = photo.name || 'foto.jpg';
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const addPhotos = async (newPhotos, onBatchProgress) => {
    // Upload in small batches (3 photos at a time) to avoid huge requests
    const BATCH = 3;
    let updated = album;
    try {
      for (let i = 0; i < newPhotos.length; i += BATCH) {
        const chunk = newPhotos.slice(i, i + BATCH);
        updated = await albumAPI.addPhotos(album.code, chunk);
        if (onBatchProgress) onBatchProgress(Math.min(i + BATCH, newPhotos.length), newPhotos.length);
      }
      // Update local context state with the full album
      await reloadAlbums();
      setLocalAlbum(updated);
      toast({ title: `${newPhotos.length} foto(s) adicionada(s)`, description: 'Marca d’água aplicada para o cliente.' });
      setAddOpen(false);
      setShareOpen(true);
    } catch (e) {
      console.error('addPhotos error', e);
      const msg = e?.response?.data?.detail || e?.message || 'Falha ao enviar fotos';
      toast({ title: 'Erro ao salvar fotos', description: msg });
    }
  };

  return (
    <div className="grain min-h-screen">
      {/* Header */}
      <header className="border-b border-[#1c1710] sticky top-0 bg-[#0b0906]/90 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/painel" className="text-[#a89e87] hover:text-[#e6dcc7] inline-flex items-center gap-1.5 text-sm">
              <ArrowLeft size={16} /> Painel
            </Link>
            <div className="h-5 w-px bg-[#2a2318]" />
            <Link to="/" className="flex items-center gap-2">
              <span className="brand-mark"><Camera size={14} strokeWidth={2.2} /></span>
              <span className="font-serif text-[18px] text-[#f2ecdf]">AXSELL</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center bg-[#12100b] border border-[#2a2318] rounded-md p-0.5">
              <button
                onClick={() => setLayout('grid')}
                className={`px-2.5 py-1.5 rounded text-xs inline-flex items-center gap-1.5 transition-colors ${layout === 'grid' ? 'bg-[#1e1810] text-[#f2ecdf]' : 'text-[#a89e87]'}`}
                title="Grade"
              >
                <Grid3x3 size={13} /> Grade
              </button>
              <button
                onClick={() => setLayout('masonry')}
                className={`px-2.5 py-1.5 rounded text-xs inline-flex items-center gap-1.5 transition-colors ${layout === 'masonry' ? 'bg-[#1e1810] text-[#f2ecdf]' : 'text-[#a89e87]'}`}
                title="Mosaico"
              >
                <Layers size={13} /> Mosaico
              </button>
            </div>
            <button onClick={copyPublicLink} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
              <Copy size={14} /> Link
            </button>
            <button onClick={extendLink} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Renovar acesso por mais 24h">
              <Clock size={14} /> +24h
            </button>
            {isOwner && (
              <button onClick={() => setEditOpen(true)} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Editar álbum">
                <Edit3 size={14} /> Editar
              </button>
            )}
            <button onClick={() => setShareOpen(true)} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
              <Share2 size={14} /> Compartilhar
            </button>
            {isOwner && (
              <button onClick={() => setAddOpen(true)} className="btn-gold px-3 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
                <Plus size={14} /> Adicionar
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87]">Galeria</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#f2ecdf] mt-1">{album.title}</h1>
            <p className="text-sm text-[#a89e87] mt-1">
              {photos.length} foto{photos.length !== 1 ? 's' : ''} • Cliente: {album.client}
              {album.date && <> • {new Date(album.date).toLocaleDateString('pt-BR')}</>}
            </p>
            <div className="text-xs mt-2 inline-flex items-center gap-1.5 text-[#a89e87]">
              <Link2 size={12} className="gold-text" />
              <span className="link-code">/album/{album.code}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#a89e87]">Receita do álbum</div>
            <div className="font-serif text-2xl gold-text">R$ {(album.revenue || 0).toFixed(2)}</div>
            <div className="text-xs text-[#7d735e]">{album.sales || 0} venda{(album.sales || 0) !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Pedidos da cliente */}
        {purchases.length > 0 && (
          <div className="card-glow rounded-xl p-5 md:p-6 mb-6 border-[#d6af6c]/30">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag size={16} className="gold-text" />
              <span className="font-medium text-[#f2ecdf]">Pedidos da cliente</span>
              <span className="text-xs text-[#7d735e]">({purchases.length} compra{purchases.length !== 1 ? 's' : ''})</span>
            </div>
            <div className="space-y-2">
              {purchases.filter(p => p.status !== 'cancelled').map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 bg-[#12100b] rounded-md px-4 py-3 border border-[#2a2318] flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-sm text-[#f2ecdf] flex items-center gap-2 flex-wrap">
                      {p.mode === 'full'
                        ? `Álbum completo (${photos.length} fotos)`
                        : p.mode === 'package'
                          ? `Pacote — ${p.count} foto${p.count !== 1 ? 's' : ''}`
                          : `${p.count} foto${p.count !== 1 ? 's' : ''} avulsa${p.count !== 1 ? 's' : ''}`}
                      {p.status === 'released' ? (
                        <span className="text-[10px] uppercase tracking-wider bg-[#d6af6c] text-[#1a140a] px-2 py-0.5 rounded font-bold">Liberado</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded font-semibold">Aguardando</span>
                      )}
                    </div>
                    <div className="text-xs text-[#7d735e]">
                      {new Date(p.createdAt).toLocaleString('pt-BR')}
                      {p.clientName && ` • ${p.clientName}`}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="gold-text font-semibold text-sm">R$ {Number(p.amount).toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.status === 'pending' && (
                      <button
                        data-testid={`release-purchase-${p.id}`}
                        onClick={async () => {
                          try {
                            await albumAPI.releasePurchase(album.code, p.id);
                            const list = await albumAPI.listPurchases(album.code);
                            setPurchases(list);
                            toast({ title: 'Download liberado', description: 'A cliente já pode baixar as fotos.' });
                          } catch (e) {
                            toast({ title: 'Erro', description: e?.response?.data?.detail || 'Tente novamente' });
                          }
                        }}
                        className="btn-gold px-3 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5"
                      >
                        <Check size={13} /> Liberar download
                      </button>
                    )}
                    {p.status === 'released' && (
                      <button
                        data-testid={`revoke-purchase-${p.id}`}
                        onClick={async () => {
                          if (!window.confirm('Reverter liberação? A cliente perderá acesso ao download mas o pedido continua ativo (você poderá liberar novamente).')) return;
                          await albumAPI.revokePurchase(album.code, p.id);
                          const list = await albumAPI.listPurchases(album.code);
                          setPurchases(list);
                          toast({ title: 'Liberação revertida', description: 'O pedido voltou para aguardando.' });
                        }}
                        className="btn-outline-dark px-3 py-2 rounded-md text-xs font-medium text-amber-300"
                      >
                        Reverter
                      </button>
                    )}
                    <button
                      data-testid={`cancel-purchase-${p.id}`}
                      onClick={async () => {
                        if (!window.confirm('Cancelar esta venda? As fotos voltarão a ficar bloqueadas e a cliente poderá selecionar novamente. Esta ação não pode ser desfeita.')) return;
                        try {
                          await albumAPI.cancelPurchase(album.code, p.id);
                          const list = await albumAPI.listPurchases(album.code);
                          setPurchases(list);
                          toast({ title: 'Venda cancelada', description: 'As fotos foram bloqueadas. A cliente pode escolher novamente.' });
                        } catch (e) {
                          toast({ title: 'Erro', description: e?.response?.data?.detail || 'Tente novamente' });
                        }
                      }}
                      className="btn-outline-dark px-3 py-2 rounded-md text-xs font-medium text-red-400"
                    >
                      <X size={13} className="inline mr-1" /> Cancelar venda
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#7d735e] mt-3">
              Confira o Pix no seu banco antes de liberar. Uma vez liberado, a cliente vê o botão de download.
            </p>
          </div>
        )}

        {photos.length === 0 ? (
          <div className="card-glow rounded-xl p-12 text-center">
            <ImageIcon size={32} className="mx-auto text-[#3a2f20] mb-3" />
            <p className="text-[#a89e87] mb-4">Nenhuma foto neste álbum ainda.</p>
            {isOwner && (
              <button onClick={() => setAddOpen(true)} className="btn-gold px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
                <Plus size={15} /> Adicionar fotos
              </button>
            )}
          </div>
        ) : layout === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
            {photos.map((p, i) => (
              <PhotoTile key={`${i}-${p.id}`} photo={p} logoUrl={currentUser?.logo} sold={soldIds.has(p.id)} onOpen={() => openAt(i)} onRemove={isOwner ? () => removePhoto(p.id) : null} onDownload={() => downloadPhoto(p)} />
            ))}
          </div>
        ) : (
          <div className="columns-2 sm:columns-3 md:columns-4 gap-2 md:gap-3 [&>*]:mb-2 md:[&>*]:mb-3">
            {photos.map((p, i) => (
              <PhotoTile key={`${i}-${p.id}`} photo={p} masonry logoUrl={currentUser?.logo} sold={soldIds.has(p.id)} onOpen={() => openAt(i)} onRemove={isOwner ? () => removePhoto(p.id) : null} onDownload={() => downloadPhoto(p)} />
            ))}
          </div>
        )}
      </main>

      {lightbox !== null && photos[lightbox] && (
        <Lightbox
          photo={photos[lightbox]}
          index={lightbox}
          total={photos.length}
          logoUrl={currentUser?.logo}
          onClose={close}
          onNext={next}
          onPrev={prev}
          onDownload={() => downloadPhoto(photos[lightbox])}
        />
      )}

      {addOpen && <AddPhotosModal existingCount={photos.length} onClose={() => setAddOpen(false)} onAdd={addPhotos} />}
      {shareOpen && <ShareModal album={album} onClose={() => setShareOpen(false)} />}
      {editOpen && <EditAlbumModal album={album} onClose={() => setEditOpen(false)} onSaved={(a) => { setLocalAlbum(a); reloadAlbums(); setEditOpen(false); toast({ title: 'Álbum atualizado' }); }} />}
    </div>
  );
}

function PhotoTile({ photo, masonry, onOpen, onRemove, onDownload, logoUrl, sold }) {
  const isVideo = photo.type === 'video';
  return (
    <div className={`group relative overflow-hidden rounded-lg bg-[#12100b] cursor-zoom-in ${masonry ? 'break-inside-avoid' : 'aspect-square'}`}>
      {isVideo ? (
        <video
          src={photo.url}
          muted
          playsInline
          preload="metadata"
          onContextMenu={(e) => e.preventDefault()}
          onClick={onOpen}
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          className={`protected-img w-full ${masonry ? 'h-auto' : 'h-full object-cover'} transition-transform duration-500 group-hover:scale-105 select-none`}
        />
      ) : (
        <img
          src={photo.url}
          alt={photo.name}
          loading="lazy"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onClick={onOpen}
          className={`protected-img w-full ${masonry ? 'h-auto' : 'h-full object-cover'} transition-transform duration-500 group-hover:scale-105 select-none`}
        />
      )}
      {/* Marca d'água (logo do fotógrafo se houver) */}
      <WatermarkOverlay logoUrl={logoUrl} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

      {sold && (
        <div className="absolute top-2 left-2 bg-[#d6af6c] text-[#1a140a] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold flex items-center gap-1 pointer-events-none z-10">
          <Check size={10} strokeWidth={3} /> Vendida
        </div>
      )}

      {isVideo && (
        <div className={`absolute ${sold ? 'top-9' : 'top-2'} left-2 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold flex items-center gap-1 pointer-events-none`}>
          <Play size={10} strokeWidth={2.5} fill="currentColor" /> Vídeo
        </div>
      )}

      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          className="w-8 h-8 rounded-md bg-black/70 hover:bg-black/85 text-[#e6dcc7] flex items-center justify-center backdrop-blur"
          title="Baixar"
        >
          <Download size={14} />
        </button>
        {onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="w-8 h-8 rounded-md bg-black/70 hover:bg-red-900/80 text-red-300 flex items-center justify-center backdrop-blur"
            title="Remover"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="absolute bottom-2 left-2 right-2 text-[11px] text-[#e6dcc7] opacity-0 group-hover:opacity-100 transition-opacity truncate">
        {photo.name}
      </div>
    </div>
  );
}

function Lightbox({ photo, index, total, onClose, onNext, onPrev, onDownload, logoUrl }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm z-10">
        <X size={20} />
      </button>

      <div className="absolute top-4 left-4 text-white/70 text-sm backdrop-blur-sm bg-black/40 px-3 py-1.5 rounded-full">
        {index + 1} / {total}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onDownload(); }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 btn-gold px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 z-10"
      >
        <Download size={15} /> Baixar
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
      >
        <ChevronRight size={22} />
      </button>

      <div className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {photo.type === 'video' ? (
          <video
            src={photo.url}
            controls
            autoPlay
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            className="protected-img max-w-full max-h-[85vh] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={photo.url}
            alt={photo.name}
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
            className="protected-img max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
          />
        )}
        {/* Marca d'água diagonal em cima da mídia */}
        <WatermarkOverlay logoUrl={logoUrl} />
      </div>
    </div>
  );
}

function AddPhotosModal({ existingCount, onClose, onAdd }) {
  const [urls, setUrls] = useState('');
  const [files, setFiles] = useState([]); // { id, dataUrl, name }
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const { toast } = useToast();

  const readFiles = async (fileList) => {
    setLoading(true);
    const arr = Array.from(fileList).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    const results = [];
    for (const f of arr) {
      try {
        if (f.type.startsWith('video/')) {
          // Videos: just read as data URL (no compression yet) — limit 20MB
          if (f.size > 20 * 1024 * 1024) {
            console.warn('vídeo muito grande', f.name, f.size);
            continue;
          }
          const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = () => rej(new Error('read error'));
            r.readAsDataURL(f);
          });
          results.push({
            id: `f${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
            dataUrl,
            name: f.name,
            kind: 'video'
          });
        } else {
          const compressed = await compressImageFile(f);
          results.push({
            id: `f${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
            dataUrl: compressed.dataUrl,
            name: f.name,
            kind: 'photo'
          });
        }
      } catch (e) {
        console.error('read fail', f.name, e);
      }
    }
    setFiles(prev => [...prev, ...results]);
    setLoading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) readFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const confirm = () => {
    const fromUrls = urls.split('\n').map(s => s.trim()).filter(Boolean).map((url, i) => ({
      id: `u${Date.now()}${i}`,
      url,
      name: `MEDIA_${existingCount + i + 1}`,
      type: /\.(mp4|mov|webm|m4v|avi)(\?|$)/i.test(url) ? 'video' : 'photo'
    }));
    const fromFiles = files.map((f, i) => ({
      id: f.id,
      url: f.dataUrl,
      name: f.name || `MEDIA_${existingCount + fromUrls.length + i + 1}`,
      type: f.kind === 'video' ? 'video' : 'photo'
    }));
    const all = [...fromFiles, ...fromUrls];
    if (!all.length) { toast({ title: 'Nenhum arquivo para adicionar' }); return; }
    onAdd(all);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Adicionar fotos e vídeos</h3>
          <button onClick={onClose} className="text-[#7d735e] hover:text-[#f2ecdf]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#a89e87] mb-5">
          A marca d’água será aplicada automaticamente nos arquivos exibidos ao cliente. Vídeos até 20MB.
        </p>

        {/* Drag & drop area */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed transition-all cursor-pointer p-8 text-center ${
            dragOver ? 'border-[#d6af6c] bg-[#1a140c]' : 'border-[#2a2318] bg-[#12100b] hover:border-[#3a2f20]'
          }`}
        >
          <Upload size={26} className={`mx-auto mb-3 ${dragOver ? 'gold-text' : 'text-[#7d735e]'}`} />
          <div className="text-sm text-[#f2ecdf] font-medium mb-1">
            Arraste fotos ou vídeos aqui ou clique para escolher
          </div>
          <div className="text-xs text-[#7d735e]">Fotos JPG/PNG/WEBP · Vídeos MP4/MOV/WEBM</div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            onChange={(e) => readFiles(e.target.files)}
          />
        </div>

        {loading && <div className="text-xs text-[#a89e87] mt-2 text-center">Processando arquivos…</div>}

        {/* Previews */}
        {files.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wider text-[#a89e87] mb-2">Prévia ({files.length})</div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {files.map(f => (
                <div key={f.id} className="relative aspect-square rounded-md overflow-hidden bg-[#12100b] group">
                  {f.kind === 'video' ? (
                    <>
                      <video src={f.dataUrl} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                      <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Film size={9} strokeWidth={2.5} /> VÍDEO
                      </div>
                    </>
                  ) : (
                    <img src={f.dataUrl} alt="" className="w-full h-full object-cover" />
                  )}
                  <div className="watermark-overlay pointer-events-none" />
                  <button
                    onClick={() => removeFile(f.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-red-300 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* URL fallback */}
        <details className="mt-5 group">
          <summary className="text-xs text-[#a89e87] cursor-pointer hover:text-[#e6dcc7] select-none">
            Ou colar URLs de imagens (uma por linha)
          </summary>
          <textarea
            rows={4}
            value={urls}
            onChange={e => setUrls(e.target.value)}
            placeholder={"https://exemplo.com/foto1.jpg\nhttps://exemplo.com/foto2.jpg"}
            className="w-full mt-2 bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf] placeholder:text-[#5c5343] font-mono"
          />
        </details>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline-dark px-4 py-2 rounded-md text-sm">Cancelar</button>
          <button onClick={confirm} className="btn-gold px-4 py-2 rounded-md text-sm font-medium">
            Adicionar {files.length > 0 ? `${files.length} arquivo${files.length !== 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ album, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/album/${album.code}`;
  const waMessage = encodeURIComponent(
    `Olá! As fotos do ensaio \"${album.title}\" já estão disponíveis para visualização. Confira e escolha suas favoritas: ${url}`
  );
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Link do cliente</h3>
          <button onClick={onClose} className="text-[#7d735e] hover:text-[#f2ecdf]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#a89e87] mb-5">
          Envie este link para seu cliente visualizar o álbum. As fotos aparecerão com marca d’água até o pagamento.
        </p>

        <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-3 mb-4 flex items-center gap-2">
          <Link2 size={15} className="gold-text flex-shrink-0" />
          <span className="text-[13px] text-[#e6dcc7] flex-1 truncate font-mono">{url}</span>
        </div>

        <button
          onClick={copy}
          className="btn-gold w-full py-3 rounded-md font-medium text-sm inline-flex items-center justify-center gap-2 mb-3"
        >
          {copied ? <><Check size={16} /> Link copiado</> : <><Copy size={16} /> Copiar link</>}
        </button>

        <a
          href={`https://wa.me/?text=${waMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline-dark w-full py-3 rounded-md font-medium text-sm inline-flex items-center justify-center gap-2"
        >
          <MessageCircle size={16} /> Enviar por WhatsApp
        </a>

        <div className="mt-5 pt-5 border-t border-[#1c1710] text-xs text-[#7d735e] space-y-1.5">
          <div className="flex items-center gap-2">
            <Check size={12} className="gold-text" /> Marca d’água aplicada em todas as fotos
          </div>
          <div className="flex items-center gap-2">
            <Check size={12} className="gold-text" /> Botão direito, arrastar e print bloqueados
          </div>
          <div className="flex items-center gap-2">
            <Check size={12} className="gold-text" /> Download liberado só após pagamento Pix
          </div>
        </div>
      </div>
    </div>
  );
}

function EditAlbumModal({ album, onClose, onSaved }) {
  const [f, setF] = useState({
    title: album.title || '',
    client: album.client || '',
    date: album.date || '',
    pricePerPhoto: album.pricePerPhoto || 0,
    packagePrice: album.packagePrice || 0,
    packageCount: album.packageCount || 10,
    extraPhotoPrice: album.extraPhotoPrice || 0,
    albumPrice: album.albumPrice || 0,
    welcomeMessage: album.welcomeMessage ?? 'Bem-vindo(a)! Escolha suas fotos favoritas com carinho.',
    instructionsMessage: album.instructionsMessage ?? 'Toque na foto para selecionar. Ao terminar.',
    thankYouMessage: album.thankYouMessage ?? 'Obrigada por escolher meu trabalho!',
    deadlineMessage: album.deadlineMessage ?? '',
    showMessages: album.showMessages !== false,
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const save = async () => {
    setSaving(true);
    try {
      const updated = await albumAPI.update(album.code, f);
      onSaved(updated);
    } catch (e) {
      toast({ title: 'Erro ao salvar', description: e?.response?.data?.detail || 'Tente novamente' });
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Editar álbum</h3>
          <button onClick={onClose} className="text-[#7d735e]"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          <Field label="Título do álbum" value={f.title} onChange={v => setF({ ...f, title: v })} />
          <Field label="Cliente" value={f.client} onChange={v => setF({ ...f, client: v })} />
          <Field label="Data" type="date" value={f.date?.slice(0,10) || ''} onChange={v => setF({ ...f, date: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preço por foto (R$)" type="number" value={f.pricePerPhoto} onChange={v => setF({ ...f, pricePerPhoto: Number(v) })} />
            <Field label="Preço do álbum (R$)" type="number" value={f.albumPrice} onChange={v => setF({ ...f, albumPrice: Number(v) })} />
            <Field label="Fotos no pacote" type="number" value={f.packageCount} onChange={v => setF({ ...f, packageCount: Number(v) })} />
            <Field label="Preço do pacote (R$)" type="number" value={f.packagePrice} onChange={v => setF({ ...f, packagePrice: Number(v) })} />
            <div className="col-span-2">
              <Field label="R$ por foto extra" type="number" value={f.extraPhotoPrice} onChange={v => setF({ ...f, extraPhotoPrice: Number(v) })} />
            </div>
          </div>

          <div className="border-t border-[#1c1710] pt-3 mt-2">
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={f.showMessages} onChange={e => setF({ ...f, showMessages: e.target.checked })} className="accent-[#d6af6c]" data-testid="edit-show-messages" />
              <span className="text-xs uppercase tracking-wider text-[#a89e87]">Exibir mensagens para o cliente</span>
            </label>
            <FieldArea label="Boas-vindas" value={f.welcomeMessage} onChange={v => setF({ ...f, welcomeMessage: v })} rows={2} />
            <FieldArea label="Instruções para selecionar" value={f.instructionsMessage} onChange={v => setF({ ...f, instructionsMessage: v })} rows={2} />
            <FieldArea label="Aviso de prazo (opcional)" value={f.deadlineMessage} onChange={v => setF({ ...f, deadlineMessage: v })} rows={2} />
            <FieldArea label="Agradecimento (após pagamento)" value={f.thankYouMessage} onChange={v => setF({ ...f, thankYouMessage: v })} rows={2} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline-dark px-4 py-2 rounded-md text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="edit-album-save" className="btn-gold px-4 py-2 rounded-md text-sm font-medium">
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldArea({ label, value, onChange, rows = 2 }) {
  return (
    <label className="block mb-2">
      <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1">{label}</span>
      <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
    </label>
  );
}

function Field({ label, type = 'text', value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
    </label>
  );
}

